import type { TIndividualSurveyQuestionnaire } from "@adgm/cross-models";
import { kycCountrySelectOptionList } from "./kyc";
import { i18nCrossUtil } from "./i18n";

// KYC信息展示键值对数据类型
export interface TKycInformationKeyValueItem {
  label: string;
  value: string;
}

// KYC信息字段类型定义
export interface TKycInformationField {
  label: string;
  name: string;
  value?: string;
}

export interface TKycInformationColumn {
  label: string;
  name: string;
}

export interface TKycInformationSection {
  title: string;
  name: string;
  type?: "table";
  fields?: TKycInformationField[];
  columns?: TKycInformationColumn[];
  tableData?: Record<string, unknown>[];
}

export interface TKycInformationIndividualData {
  nationality?: string;
  individualSurveyQuestionnaire?: TIndividualSurveyQuestionnaire;
}

/**
 * 处理KYC信息展示字段值，将各种数据类型转换为用户友好的字符串格式
 *
 * 该函数专门用于KYC信息展示页面，负责将后端返回的各种类型数据转换为
 * 前端展示所需的标准化字符串格式，确保用户界面的一致性和可读性
 *
 * 处理规则：
 * - null/undefined: 转换为 "-" 表示无数据
 * - 数组: 使用换行符连接各元素，适合多选项展示
 * - 对象: 优先提取 name 或 label 属性，否则展示所有键值对
 * - 其他类型: 直接转换为字符串
 *
 * @param fieldValue 需要处理的字段值，可以是任意类型
 * @returns 转换后的用户友好字符串，用于KYC信息页面展示
 */
export const processKycInformationFieldValue = (fieldValue: unknown): string => {
  if (fieldValue === null || fieldValue === undefined) {
    return "-";
  } else if (Array.isArray(fieldValue)) {
    return fieldValue.join("\n");
  } else if (typeof fieldValue === "object") {
    // 如果是对象，处理特殊属性或将键值对用换行符分隔
    const obj = fieldValue as Record<string, unknown>;
    if (obj.name) {
      return String(obj.name);
    } else if (obj.label) {
      return String(obj.label);
    }
    // 将对象的键值对用换行符分隔
    return Object.entries(obj)
      .map(([key, val]) => `${key}: ${String(val)}`)
      .join("\n");
  }
  return String(fieldValue);
};

/**
 * 将个人数据映射到字段配置
 * @param fields 字段配置数组
 * @param sectionName section名称
 * @param individualData 个人数据
 * @returns 映射后的键值对数据
 */
export const mapIndividualData = (
  fields: TKycInformationField[],
  sectionName: string,
  individualData: TKycInformationIndividualData,
): TKycInformationKeyValueItem[] => {
  const questionnaire = individualData.individualSurveyQuestionnaire || {};
  const sectionData = (questionnaire as Record<string, unknown>)[sectionName] || {};

  return fields.map((field: TKycInformationField) => {
    let fieldValue: unknown = "";

    // 首先检查 section 内部的数据
    if (typeof sectionData === "object" && sectionData !== null && field.name in sectionData) {
      fieldValue = (sectionData as Record<string, unknown>)[field.name];
    }
    // 然后检查最外层的数据（如nationality）
    else if ((individualData as Record<string, unknown>)[field.name]) {
      fieldValue = (individualData as Record<string, unknown>)[field.name];
    }

    // NO作为选项,不进行翻译~
    return {
      label: field.label,
      value:
        getKycInformationNationalityValue(
          processKycInformationFieldValue(fieldValue),
          field.name,
        ) || "No",
    };
  });
};

/**
 * 将机构数据映射到字段配置
 * @param fields 字段配置数组
 * @param sectionName section名称
 * @param institutionData 机构数据
 * @returns 映射后的键值对数据
 */
export const mapInstitutionData = (
  fields: TKycInformationField[],
  sectionName: string,
  institutionData: any,
): TKycInformationKeyValueItem[] => {
  let sectionData: Record<string, unknown> = {};

  switch (sectionName) {
    case "institutionSurveyQuestionnaire":
      sectionData = institutionData.institutionSurveyQuestionnaire || {};
      break;
    case "registration":
      sectionData = institutionData.institution?.registration || {};
      break;
    case "operations":
      sectionData = institutionData.institution?.operations || {};
      break;
    case "mostSeniorAuthorisedSignatory":
      sectionData = institutionData.institution?.mostSeniorAuthorisedSignatory || {};
      break;
  }

  return fields.map((field: TKycInformationField) => {
    const fieldValue = sectionData[field.name];
    // NO作为选项,不进行翻译~
    return {
      label: field.label,
      value:
        getKycInformationNationalityValue(
          processKycInformationFieldValue(fieldValue),
          field.name,
        ) || "No",
    };
  });
};

/**
 * 获取机构section数据（用于机构类型的数组数据）
 * @param sectionName section名称
 * @param institutionData 机构数据
 * @returns 机构section数据数组
 */
export const getInstitutionSectionData = (
  sectionName: string,
  institutionData: any,
): Record<string, unknown>[] => {
  switch (sectionName) {
    case "shareholders":
      return (institutionData.institution?.shareholders || []).map(
        item => item as unknown as Record<string, unknown>,
      );
    case "beneficiaries":
      return (institutionData.institution?.beneficiaries || []).map(
        item => item as unknown as Record<string, unknown>,
      );
    case "directorsOrAuthorisedSignatories":
      return (institutionData.institution?.directorsOrAuthorisedSignatories || []).map(
        item => item as unknown as Record<string, unknown>,
      );
    default:
      return [];
  }
};

/**
 * 将 individualData 映射到 informationArray 结构的方法
 * @param individualData 个人数据
 * @param informationArray 信息配置数组
 * @returns 处理后的sections数组
 */
export const mapIndividualDataToInformationArray = (
  individualData: TKycInformationIndividualData,
  informationArray: TKycInformationSection[],
): TKycInformationSection[] => {
  return informationArray.map(section => {
    // 从 individualSurveyQuestionnaire 中获取 section 数据
    const questionnaire = individualData.individualSurveyQuestionnaire || {};
    const sectionData = questionnaire[section.name] || {};

    const updatedFields =
      section.fields?.map((field: TKycInformationField): TKycInformationField => {
        let fieldValue: unknown = "";

        // 首先检查 section 内部的数据
        if (sectionData[field.name]) {
          fieldValue = sectionData[field.name];
        }
        // 然后检查最外层的数据
        else if (individualData[field.name as keyof TKycInformationIndividualData]) {
          fieldValue = individualData[field.name as keyof TKycInformationIndividualData];
        }

        return {
          ...field,
          value: processKycInformationFieldValue(fieldValue),
        };
      }) || [];

    return {
      ...section,
      fields: updatedFields,
    };
  });
};

/**
 * 将 institutionData 映射到 informationArray 结构的方法
 * @param institutionData 机构数据
 * @param informationArray 信息配置数组
 * @returns 处理后的sections数组
 */
export const mapInstitutionDataToInformationArray = (
  institutionData: any,
  informationArray: TKycInformationSection[],
): TKycInformationSection[] => {
  return informationArray.map(section => {
    if (section.type === "table") {
      // 处理表格类型的数据 - 根据 section.name 映射
      let tableData: Record<string, unknown>[] = [];

      // 根据 section.name 映射数据
      switch (section.name) {
        case "shareholders":
          tableData = (institutionData.institution?.shareholders || []).map(
            item => item as unknown as Record<string, unknown>,
          );
          break;
        case "beneficiaries":
          tableData = (institutionData.institution?.beneficiaries || []).map(
            item => item as unknown as Record<string, unknown>,
          );
          break;
        case "directorsOrAuthorisedSignatories":
          tableData = (institutionData.institution?.directorsOrAuthorisedSignatories || []).map(
            item => item as unknown as Record<string, unknown>,
          );
          break;
      }

      return {
        ...section,
        tableData,
      };
    }
    // 处理普通字段 - 根据 section.name 获取对应数据
    let sectionData: Record<string, unknown> = {};

    switch (section.name) {
      case "institutionSurveyQuestionnaire":
        sectionData = institutionData.institutionSurveyQuestionnaire || {};
        break;
      case "registration":
        sectionData = institutionData.institution?.registration || {};
        break;
      case "operations":
        sectionData = institutionData.institution?.operations || {};
        break;
      case "mostSeniorAuthorisedSignatory":
        sectionData = institutionData.institution?.mostSeniorAuthorisedSignatory || {};
        break;
    }

    const updatedFields =
      section.fields?.map((field: TKycInformationField): TKycInformationField => {
        const fieldValue: unknown = sectionData[field.name];

        return {
          ...field,
          value: processKycInformationFieldValue(fieldValue),
        };
      }) || [];

    return {
      ...section,
      fields: updatedFields,
    };
  });
};

/**
 * 用于KYC信息展示页面 根据国家简称获取国家全称
 * @param value 值
 * @param name 字段名
 * @returns 处理后的值
 */
const nationalitiesMappedDisplay = (value: unknown) => {
  const countriesOrigin = kycCountrySelectOptionList("nationality");
  let targetNationality;
  if (Array.isArray(value)) {
    targetNationality = countriesOrigin.filter(option => value.includes(option.value));
  } else {
    targetNationality = countriesOrigin.find(option => option.value === value);
  }
  return Array.isArray(targetNationality)
    ? targetNationality.map(option => option.title).join(" / ")
    : targetNationality?.title;
};

export const getKycInformationNationalityValue = (value: unknown, name: string): string => {
  if (
    name === "nationalities" ||
    name === "nationality" ||
    name === "fundingSourceCountry" ||
    name === "incorporationPlace" ||
    name === "country" ||
    name === "operatingCountries" ||
    name === "multipleNationalities"
  ) {
    return nationalitiesMappedDisplay(value);
  }
  if (value === null || value === undefined) {
    return "-";
  }
  return String(value);
};

/**
 * 处理 Record 类型字段，转换为表格数据
 * 用于将 Record<string, string> 类型的数据转换为表格展示所需的数组格式
 *
 * @param record Record 类型的字段值
 * @returns 转换后的表格数据数组，包含 product 和 value 字段
 */
export const formatRecordToTableData = (
  record: Record<string, string> | undefined | null,
): { product: string; value: string }[] => {
  if (!record) return [];
  return Object.entries(record).map(([key, value]) => ({
    product: key,
    value: value || "-",
  }));
};

/**
 * 处理数组字段，转换为换行字符串
 * 用于将字符串数组转换为多行文本展示格式
 *
 * @param value 字符串数组，如果传入非数组类型则返回 "-"
 * @returns 使用换行符连接的字符串，空数组或非数组类型返回 "-"
 */
export const formatArrayValue = (value: string[] | undefined): string => {
  // 参数校验：检查是否为数组类型
  if (!Array.isArray(value)) {
    console.log("formatArrayValue value", value);
    return "-";
  }
  // 检查数组是否为空
  if (value.length === 0) {
    return "-";
  }
  return value.join(";\n");
};

/**
 * 处理 listedEntity 对象，显示完整信息（name、exchange、country）
 * 用于格式化上市公司实体信息，将对象属性转换为多行文本展示
 *
 * @param entity listedEntity 对象，包含 name、exchange、country 等属性
 * @returns 格式化后的多行文本字符串，包含所有非空属性信息
 */
export const formatListedEntity = (
  entity: { name?: string; exchange?: string; country?: string } | null | undefined,
): string => {
  if (!entity) return "-";

  const crossIntl = i18nCrossUtil.t();

  const parts: string[] = [];

  if (entity.name) {
    parts.push(
      `${crossIntl["kyc.institution.basicInformation.listedEntity.name"]}: ${entity.name}`,
    );
  }

  if (entity.exchange) {
    parts.push(
      `${crossIntl["kyc.institution.basicInformation.listedEntity.exchange"]}: ${entity.exchange}`,
    );
  }

  if (entity.country) {
    const countryValue = getKycInformationNationalityValue(entity.country, "country");
    parts.push(
      `${crossIntl["kyc.institution.basicInformation.listedEntity.country"]}: ${countryValue || entity.country}`,
    );
  }

  return parts.length > 0 ? parts.join("\n") : "-";
};
